<template>
  <div>
    <v-btn
      color="primary"
      :loading="loading"
      :disabled="loading || isSigning"
      @click="initierSignature"
    >
      Signer le document
    </v-btn>

    <v-dialog
      v-model="dialog"
      max-width="900"
      persistent
      @click:outside="onCancel"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Signature électronique</span>
          <v-btn icon="mdi-close" variant="text" @click="onCancel" />
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-0">
          <div ref="embeddedContainer" style="width: 100%; height: 600px;" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, nextTick, onBeforeUnmount } from 'vue';
import HelloSign from 'hellosign-embedded';

const props = defineProps({
  signerEmail: { type: String, required: true },
  signerName: { type: String, required: true },
  clientId: { type: String, required: true },   // ✅ passer le clientId en prop
  createEndpoint: {
    type: String,
    default: 'https://my.edisac.com/api/dropbox-sign/create-embedded',
  },
  getUrlEndpoint: {
    type: String,
    default: 'https://my.edisac.com/api/dropbox-sign/get-sign-url',
  },
  numero:{ type: String },
  file : { type: String }
});

const emit = defineEmits(['signed', 'cancelled', 'error']);

const dialog = ref(false);
const loading = ref(false);
const isSigning = ref(false);
const embeddedContainer = ref(null);
const snackbar = ref({ show: false, text: '', color: 'info' });

let helloSignClient = null;

async function initierSignature() {
  loading.value = true;

  try {
    // 1. Créer la demande → signatureId
    const createRes = await fetch(props.createEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: props.signerEmail,
        name: props.signerName,
        numero : props.numero,
        file : props.file
      }),
    });

    if (!createRes.ok) {
      const err = await createRes.json().catch(() => ({}));
      throw new Error(err.error || 'Erreur création demande');
    }

    const { signatureId } = await createRes.json();
    console.log('✅ signatureId reçu:', signatureId);

    if (!signatureId) {
      throw new Error('signatureId manquant');
    }

    // 2. Ouvrir le dialog immédiatement
    dialog.value = true;
    isSigning.value = true;
    await nextTick();

    // 3. Générer le signUrl AU DERNIER MOMENT
    const urlRes = await fetch(props.getUrlEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ signatureId }),  // ✅ utilise la variable locale
    });

    if (!urlRes.ok) {
      const err = await urlRes.json().catch(() => ({}));
      throw new Error(err.error || 'Erreur génération signUrl');
    }

    const { signUrl } = await urlRes.json();
    console.log('✅ signUrl reçue:', signUrl);

    if (!signUrl) {
      throw new Error('signUrl manquante');
    }

    // 4. Ouvrir l'iframe
    helloSignClient = new HelloSign({ clientId: props.clientId });

    helloSignClient.on('sign', () => {
      showSnackbar('Document signé avec succès !', 'success');
      emit('signed');
      closeDialog();
    });

    helloSignClient.on('cancel', () => {
      emit('cancelled');
      closeDialog();
    });

    helloSignClient.on('error', (err) => {
      console.error('Erreur HelloSign:', err);
      showSnackbar('Erreur lors de la signature', 'error');
      emit('error', err);
    });

    helloSignClient.open(signUrl, {
      container: embeddedContainer.value,
      testMode: true,
      skipDomainVerification: true,
    });
  } catch (error) {
    console.error('Erreur signature:', error);
    showSnackbar(error.message, 'error');
    emit('error', error);
    closeDialog();
  } finally {
    loading.value = false;
  }
}

function closeDialog() {
  if (helloSignClient) {
    try { helloSignClient.close(); } catch (e) {}
    helloSignClient = null;
  }
  dialog.value = false;
  isSigning.value = false;
}

function onCancel() {
  emit('cancelled');
  closeDialog();
}

function showSnackbar(text, color = 'info') {
  snackbar.value = { show: true, text, color };
}

onBeforeUnmount(() => closeDialog());
</script>